import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import {
  CheckCircle,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Loader2,
  X,
  XCircle,
} from "lucide-react";
import PropTypes from "prop-types";
import { memo, useCallback, useEffect, useState } from "react";

/**
 * DeepResearchBlock - Displays deep research progress and results in a collapsible format
 * Similar to ReasoningBlock but with progress tracking, activity feed, and sources
 */
const DeepResearchBlock = memo(
  ({
    isStreaming = false,
    isComplete = false,
    isAborted = false,
    activity = [],
    sources = [],
    progressPercentage = 0,
    elapsedTime = 0,
    activitiesCount = 0,
    sourcesCount = 0,
    onAbort,
    className,
    messageId,
  }) => {
    // Get preference key for this research block
    const preferenceKey = messageId ? `deep-research-block-${messageId}` : null;

    // Initialize state - auto-open while streaming, otherwise use saved preference
    const [isOpen, setIsOpen] = useState(() => {
      if (isStreaming) return true;
      if (!preferenceKey) return false;
      const saved = localStorage.getItem(preferenceKey);
      return saved !== null ? saved === "true" : false;
    });

    // Live timer state - updates every second while streaming
    const [currentElapsedTime, setCurrentElapsedTime] = useState(elapsedTime);

    // Update live timer every second while streaming
    useEffect(() => {
      if (isStreaming) {
        const interval = setInterval(() => {
          setCurrentElapsedTime((prev) => prev + 1000);
        }, 1000);
        return () => clearInterval(interval);
      } else {
        // Use final elapsed time when not streaming
        setCurrentElapsedTime(elapsedTime);
      }
    }, [isStreaming, elapsedTime]);

    // Auto-open when streaming starts
    useEffect(() => {
      if (isStreaming) {
        setIsOpen(true);
      }
    }, [isStreaming]);

    // Auto-close 2 seconds after completion (unless user has interacted)
    useEffect(() => {
      if (isComplete && isOpen && !isStreaming) {
        const timer = setTimeout(() => {
          // Only auto-close if user hasn't saved a preference to keep it open
          if (preferenceKey) {
            const saved = localStorage.getItem(preferenceKey);
            if (saved !== "true") {
              setIsOpen(false);
            }
          } else {
            setIsOpen(false);
          }
        }, 2000);
        return () => clearTimeout(timer);
      }
    }, [isComplete, isOpen, isStreaming, preferenceKey]);

    // Handle user toggle with preference persistence
    const handleToggle = useCallback(
      (newState) => {
        setIsOpen(newState);
        if (preferenceKey) {
          localStorage.setItem(preferenceKey, String(newState));
        }
      },
      [preferenceKey]
    );

    // Format elapsed time
    const formatTime = useCallback((ms) => {
      const seconds = Math.floor(ms / 1000);
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${seconds}s`;
    }, []);

    // Get status icon and text
    const getStatusDisplay = () => {
      if (isAborted) {
        return {
          icon: <XCircle className="h-4 w-4 text-red-500" />,
          text: "Research stopped",
          color: "text-red-600 dark:text-red-400",
        };
      }
      if (isStreaming) {
        // Show different text if no activity yet (initializing)
        const isInitializing = activity.length === 0;
        const liveTimeStr = formatTime(currentElapsedTime);
        return {
          icon: <Loader2 className="h-4 w-4 animate-spin text-blue-500" />,
          text: isInitializing
            ? `Preparing deep research... ${liveTimeStr}`
            : `Deep research in progress - ${liveTimeStr}`,
          color: "text-blue-600 dark:text-blue-400",
          showProgress: !isInitializing && progressPercentage > 0,
        };
      }
      if (isComplete) {
        return {
          icon: <CheckCircle className="h-4 w-4 text-muted-foreground" />,
          text: `Research completed in ${formatTime(elapsedTime)}`,
          color: "text-foreground",
        };
      }
      return {
        icon: null,
        text: "Research",
        color: "text-muted-foreground",
      };
    };

    const status = getStatusDisplay();

    // Show first 10 sources with expandable "show more"
    const [showAllSources, setShowAllSources] = useState(false);
    const visibleSources = showAllSources ? sources : sources.slice(0, 10);
    const hasMoreSources = sources.length > 10;

    return (
      <div
        className={cn(
          "deep-research-block my-3 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden transition-all duration-200",
          className
        )}
      >
        <Collapsible open={isOpen} onOpenChange={handleToggle}>
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              className="w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors min-h-[44px] rounded-t-lg rounded-b-none h-auto font-normal"
              aria-label={`${isOpen ? "Collapse" : "Expand"} deep research progress`}
              aria-expanded={isOpen}
            >
              <div className="flex items-center gap-2 flex-1">
                {status.icon}

                <span className={cn("text-sm font-medium", status.color)}>
                  {status.text}
                </span>

                {status.showProgress && (
                  <span className="text-xs text-blue-600 dark:text-blue-400 font-medium bg-blue-50 dark:bg-blue-900/20 px-2 py-0.5 rounded-full">
                    {Math.round(progressPercentage)}%
                  </span>
                )}

                {sourcesCount > 0 && (
                  <span className="text-xs text-green-600 dark:text-green-400 font-medium bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded-full">
                    {sourcesCount} {sourcesCount === 1 ? "source" : "sources"}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2">
                {isStreaming && onAbort && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAbort();
                    }}
                    className="h-8 px-2 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400"
                    aria-label="Stop research"
                  >
                    <X className="h-4 w-4 mr-1" />
                    <span className="text-xs">Stop</span>
                  </Button>
                )}

                {isOpen ? (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                )}
              </div>
            </Button>
          </CollapsibleTrigger>

          <CollapsibleContent className="overflow-hidden transition-all duration-300 ease-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0">
            <div className="border-t border-border p-3 space-y-3 bg-sidebar">
              {/* Progress Bar (while streaming) */}
              {isStreaming && (
                <div className="space-y-1">
                  <Progress
                    value={progressPercentage}
                    className="h-1.5 bg-gray-200 dark:bg-gray-700"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>
                      {activitiesCount}{" "}
                      {activitiesCount === 1 ? "activity" : "activities"}
                    </span>
                    <span className="tabular-nums">
                      {Math.round(progressPercentage)}%
                    </span>
                  </div>
                </div>
              )}

              {/* Activity Log */}
              {activity.length > 0 && (
                <div className="space-y-1.5">
                  <div className="text-xs font-medium text-foreground">
                    {isStreaming ? "Recent Activity" : "Activity Log"}
                  </div>
                  <div className="space-y-1 max-h-32 sm:max-h-40 overflow-y-auto bg-sidebar rounded-md p-2 border border-border scrollbar-thin scrollbar-thumb-rounded">
                    {activity.map((act, idx) => (
                      <div
                        key={idx}
                        className="text-xs text-foreground flex items-start gap-2"
                      >
                        <span className="text-muted-foreground mt-0.5">•</span>
                        <span className="flex-1">
                          {act.message || act.text || act}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sources - Show in separate scrollable area */}
              {sources.length > 0 && (
                <div className="space-y-1.5">
                  <div className="text-xs font-medium text-foreground flex items-center gap-2">
                    <span>Sources</span>
                    <span className="text-xs text-green-600 dark:text-green-400 font-medium bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded-full">
                      {sources.length}
                    </span>
                  </div>
                  <div className="space-y-1.5 max-h-40 sm:max-h-48 overflow-y-auto bg-sidebar rounded-md p-2 border border-border scrollbar-thin scrollbar-thumb-rounded">
                    {visibleSources.map((source, idx) => (
                      <a
                        key={idx}
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 hover:underline flex items-start gap-1.5 group min-h-[44px] sm:min-h-0 p-1.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors"
                      >
                        <ExternalLink className="h-3 w-3 mt-0.5 flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity" />
                        <span className="flex-1 break-words line-clamp-2">
                          {source.title || source.url}
                        </span>
                      </a>
                    ))}
                    {hasMoreSources && !showAllSources && (
                      <button
                        onClick={() => setShowAllSources(true)}
                        className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline min-h-[44px] sm:min-h-0 flex items-center w-full justify-center py-2"
                      >
                        Show {sources.length - 10} more sources
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Elapsed Time (when complete) */}
              {isComplete && elapsedTime > 0 && (
                <div className="text-xs text-foreground pt-1 border-t border-border">
                  Total time: {formatTime(elapsedTime)}
                </div>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>
    );
  }
);

DeepResearchBlock.displayName = "DeepResearchBlock";

DeepResearchBlock.propTypes = {
  isStreaming: PropTypes.bool,
  isComplete: PropTypes.bool,
  isAborted: PropTypes.bool,
  activity: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        message: PropTypes.string,
        text: PropTypes.string,
        timestamp: PropTypes.string,
      }),
    ])
  ),
  sources: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string.isRequired,
      title: PropTypes.string,
      snippet: PropTypes.string,
    })
  ),
  progressPercentage: PropTypes.number,
  elapsedTime: PropTypes.number,
  activitiesCount: PropTypes.number,
  sourcesCount: PropTypes.number,
  onAbort: PropTypes.func,
  className: PropTypes.string,
  messageId: PropTypes.string,
};

export default DeepResearchBlock;
